<script lang="ts">
  import autosize from 'autosize'
  import { autofocus } from '#app/lib/components/actions/autofocus'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { icon } from '#app/lib/icons'
  import { getActionKey } from '#app/lib/key_events'
  import { userContent } from '#app/lib/user_content'
  import Spinner from '#components/spinner.svelte'
  import { updateElement, type ListingElementWithEntity } from '#listings/lib/listings'
  import { I18n, i18n } from '#user/lib/i18n'
  import type { SerializedUser } from '#users/lib/users'

  let {
    element = $bindable(),
    creator,
    flash = $bindable(),
    isCreatorMainUser,
  }: {
    element: ListingElementWithEntity
    creator: SerializedUser
    flash: FlashState
    isCreatorMainUser: boolean
  } = $props()

  let isEditMode = $state(false)
  let validating = $state(false)
  let newComment = $state(element.comment)

  async function validate () {
    validating = true
    try {
      await _updateElement()
    } catch (err) {
      flash = err
    } finally {
      validating = false
    }
    toggleEditMode()
  }

  function onKeyDown (e) {
    const key = getActionKey(e)
    if (key === 'esc') isEditMode = false
    else if (e.ctrlKey && key === 'enter') validate()
    e.stopPropagation()
  }

  function toggleEditMode () {
    isEditMode = !isEditMode
    flash = null
  }

  const comment = $derived(element.comment)
  const username = $derived(creator.username)

  async function _updateElement () {
    flash = null
    try {
      await updateElement({ id: element._id, comment: newComment })
      element.comment = newComment
    } catch (err) {
      flash = err
    }
  }
</script>

<div class="listing-element-comment">
  {#if isEditMode}
    <label>
      {i18n('Comment by %{username}', { username })}
      <textarea
        type="text"
        bind:value={newComment}
        use:autosize
        use:autofocus
        onkeydown={onKeyDown}
      ></textarea>
    </label>
    <div class="button-group-right">
      <button
        class="tiny-button cancel"
        onclick={toggleEditMode}
      >
        {I18n('cancel')}
      </button>

      <button
        class="tiny-button validate success"
        title={I18n('validate')}
        disabled={validating}
        onclick={validate}
      >
        {#if validating}
          <Spinner />
        {:else}
          {@html icon('check')}
          {I18n('save')}
        {/if}
      </button>
    </div>
  {:else}
    {#if isCreatorMainUser}
      <button
        onclick={toggleEditMode}
        title={i18n('edit')}
        class="edit-button"
      >
        <div class="comment-header">
          <span class="section-label">{i18n('Comment by %{username}', { username })}</span>
          {@html icon('pencil')}
        </div>
        {#if comment}
          <p class="comment">
            {@html userContent(comment)}
          </p>
        {/if}
      </button>
    {:else}
      {#if comment}
        <p class="section-label">
          {i18n('Comment by %{username}', { username })}
        </p>
        <p>{@html userContent(comment)}</p>
      {/if}
    {/if}
  {/if}
</div>

<style lang="scss">.listing-element-comment {
  background-color: #f5f5f5;
  padding: 0.5em 1em;
  margin-block-start: 1.5em;
  border-radius: 2px;
}

.edit-button {
  width: 100%;
}
.edit-button :global(.fa) {
  opacity: 0.5;
  transition: opacity 0.5s 0s ease;
}
.edit-button:hover :global(.fa) {
  opacity: 1;
}

.comment-header {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.comment {
  text-align: start;
  font-size: 1rem;
  margin-block-start: 0.5em;
}

.section-label {
  color: #959595;
}</style>
